import { type ExcalidrawFreeDrawElement } from "@excalidraw/element/types";
import { getCommonBounds } from "@excalidraw/element";
import { TOOL_TYPE } from "@excalidraw/common";

export const recognizeShape = (
    element: ExcalidrawFreeDrawElement,
): typeof TOOL_TYPE[keyof typeof TOOL_TYPE] | null => {
    const points = element.points;
    if (points.length < 5) {
        return null;
    }

    const [minX, minY, maxX, maxY] = getCommonBounds([element]);
    const width = Math.max(1, maxX - minX);
    const height = Math.max(1, maxY - minY);

    if (width < 10 || height < 10) {
        return null;
    }

    const localMinX = minX - element.x;
    const localMinY = minY - element.y;
    const centerX = localMinX + width / 2;
    const centerY = localMinY + height / 2;

    const startPoint = points[0];
    const endPoint = points[points.length - 1];
    const distStartEnd = Math.sqrt(
        Math.pow(startPoint[0] - endPoint[0], 2) +
        Math.pow(startPoint[1] - endPoint[1], 2),
    );

    const isClosed = distStartEnd < Math.max(width, height) * 0.2;

    // Normalize points to [-1, 1] range relative to the center of the bounding box
    const normalizedPoints = points.map(([px, py]) => [
        (px - centerX) / (width / 2),
        (py - centerY) / (height / 2),
    ]);

    if (!isClosed) {
        // Simple heuristic for line
        let lineResidual = 0;
        const xMean =
            normalizedPoints.reduce((a, b) => a + b[0], 0) / normalizedPoints.length;
        const yMean =
            normalizedPoints.reduce((a, b) => a + b[1], 0) / normalizedPoints.length;

        let num = 0;
        let den = 0;
        for (const [x, y] of normalizedPoints) {
            num += (x - xMean) * (y - yMean);
            den += (x - xMean) ** 2;
        }
        const slope = den === 0 ? 0 : num / den;
        const intercept = yMean - slope * xMean;

        for (const [x, y] of normalizedPoints) {
            const projectedY = slope * x + intercept;
            lineResidual += (y - projectedY) ** 2;
        }
        lineResidual /= normalizedPoints.length;

        if (lineResidual < 0.05) {
            return TOOL_TYPE.line;
        }
    }

    if (isClosed) {
        let ellipseError = 0;
        let rectError = 0;
        let diamondError = 0;

        for (const [nx, ny] of normalizedPoints) {
            const distFromEllipse = Math.abs(Math.sqrt(nx * nx + ny * ny) - 1);
            ellipseError += distFromEllipse;

            const distFromRect = Math.abs(Math.max(Math.abs(nx), Math.abs(ny)) - 1);
            rectError += distFromRect;

            const distFromDiamond = Math.abs(Math.abs(nx) + Math.abs(ny) - 1);
            diamondError += distFromDiamond;
        }

        ellipseError /= points.length;
        rectError /= points.length;
        diamondError /= points.length;

        if (ellipseError < rectError && ellipseError < diamondError) {
            if (ellipseError < 0.15) {
                return TOOL_TYPE.ellipse;
            }
        } else if (rectError < diamondError) {
            if (rectError < 0.15) {
                return TOOL_TYPE.rectangle;
            }
        } else if (diamondError < 0.15) {
            return TOOL_TYPE.diamond;
        }
    }

    return null;
};
